import { CurrenciesContext } from '@/currencies/currency.context';
import { useWallets } from '@/devtools/hooks/useWallets.hook';
import BigNumber from 'bignumber.js';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface WalletsData
  extends Array<{
    label: string;
    value: string; // wallet id
    asset: string;
    balance: string;
    accountNumber: string;
    iconUrl: string;
  }> {}

export interface WalletData {
  label: string;
  value: string;
  asset: string;
  balance: string;
  accountNumber: string;
  iconUrl: string;
}

interface UseAccountSelectorParams {
  asset?: string;
  value?: string;
  type?: string;
  checkBalance?: boolean;
  onValueChange: (value: string) => void;
  onWalletChange?: (value: WalletData | undefined) => void;
}

export const useAccountSelector = ({
  asset,
  value,
  type,
  checkBalance = true,
  onValueChange,
  onWalletChange
}: UseAccountSelectorParams) => {
  const { t } = useTranslation('earn');
  const currencies = useContext(CurrenciesContext);
  const [searchText, setSearchText] = useState('');

  const wallets = useWallets({ enabled: false, noSave: true, search: searchText });
  useEffect(() => {
    wallets.wallets.refetch();
  }, [searchText]);

  const walletsData = useMemo(() => {
    if (!wallets.wallets.data?.data) return [];
    let data: WalletsData = [];

    wallets.wallets.data?.data.forEach((wallet) => {
      if (asset && wallet.asset !== asset) return;
      if (type && wallet.type !== type) return;
      wallet.wallets.forEach((account) => {
        if (checkBalance && new BigNumber(account.balance).lte(0)) return;
        data.push({
          value: account.id,
          label: account.accountLabel ? t(`earn-invest::glossary.${account.accountLabel}`) : account.accountName || ' - ',
          asset: wallet.asset,
          balance: account.balance,
          accountNumber: account.accountNumber,
          iconUrl: currencies.currenciesDict?.[wallet.asset]?.token_image_url || ''
        });
      });
    });

    if (data.length === 0) return data;

    data = data.sort((a, b) => Number(b.balance) - Number(a.balance));

    return data;
  }, [wallets, asset]);

  const [hasFocus, setHasFocus] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState(value || '');

  const selectedWallet: WalletData | undefined = useMemo(() => {
    return walletsData.find((wallet) => wallet.value === selectedWalletId);
  }, [value, selectedWalletId]);

  const displayWalletData = walletsData;

  useEffect(() => {
    if (value) setSelectedWalletId(value);
  }, [value]);

  useEffect(() => {
    if (!!selectedWallet && !!onWalletChange) {
      onWalletChange(selectedWallet);
    }
  }, [selectedWallet]);

  useEffect(() => {
    if (!hasFocus) return;
    const close = () => {
      setHasFocus(false);
    };
    document.addEventListener('click', close);
    return () => {
      document.removeEventListener('click', close);
    };
  }, [hasFocus]);

  const changeFocus = (value: boolean) => setHasFocus(value);
  const changeSearchText = (value: string) => setSearchText(value);
  const selectWalletId = (id: string) => {
    setSelectedWalletId(id);
    onValueChange(id);
  };
  const getDecimalsByAsset = (asset: string) => {
    return currencies.currenciesDict?.[asset]?.decimals || 0;
  };

  return {
    hasFocus,
    selectedWallet,
    selectedWalletId,
    displayWalletData,
    changeFocus,
    changeSearchText,
    selectWalletId,
    getDecimalsByAsset
  };
};
